import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEPRECIATION_METHODS,
  DEPRECIATION_METHOD_MAP,
  type DepreciationMethod,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { Badge, EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';

interface Category {
  id: string;
  name: string;
  method: DepreciationMethod;
  rate: number;
  usefulLifeMonths: number;
  assetAccountId: string;
  accumulatedAccountId: string;
  expenseAccountId: string;
  isActive: boolean;
  assetCount: number;
}

interface AccountOption {
  id: string;
  code: string;
  name: string;
}

/**
 * تصنيفات الأصول.
 *
 * التصنيف يحمل سياسة الإهلاك وحسابات الترحيل الثلاثة. وجوده يمنع تكرارها
 * على كل أصل، ويجعل تغيير السياسة موضعًا واحدًا لا مئة صف.
 */
export function AssetCategories() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);

  const categories = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => api<Category[]>('/assets/categories'),
  });

  const accounts = useQuery({
    queryKey: ['accounting', 'postable'],
    queryFn: () => api<AccountOption[]>('/accounting/accounts/postable'),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['assets'] });

  const seed = useMutation({
    mutationFn: () =>
      api<{ added: number; skipped: number }>('/assets/categories/seed', { method: 'POST' }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/assets/categories/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const byId = new Map((accounts.data ?? []).map((a) => [a.id, a]));
  const rows = categories.data ?? [];

  return (
    <div className="page">
      <PageHead
        title="تصنيفات الأصول"
        subtitle="سياسة الإهلاك وحسابات الترحيل"
        actions={
          <>
            <Link to="/assets" className="btn">
              سجل الأصول
            </Link>
            {rows.length === 0 && (
              <button className="btn" onClick={() => seed.mutate()} disabled={seed.isPending}>
                {seed.isPending ? 'جارٍ' : 'إضافة التصنيفات المقترحة'}
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              تصنيف جديد
            </button>
          </>
        }
      />

      {seed.isError && (
        <div className="alert alert-danger mb-4">
          {seed.error instanceof Error ? seed.error.message : 'تعذّرت الإضافة.'}
        </div>
      )}

      {remove.isError && (
        <div className="alert alert-danger mb-4">
          {remove.error instanceof Error ? remove.error.message : 'تعذّر الحذف.'}
        </div>
      )}

      {categories.isError && (
        <ErrorState error={categories.error} onRetry={() => categories.refetch()} />
      )}

      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>التصنيف</th>
                <th>الطريقة</th>
                <th>النسبة / العمر</th>
                <th>حساب الأصل</th>
                <th>الأصول</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className={c.isActive ? 'cell-strong' : 'cell-mute'}>{c.name}</span>
                    {!c.isActive && <div className="tiny mute">معطّل</div>}
                  </td>
                  <td className="cell-mute">{DEPRECIATION_METHOD_MAP[c.method].label}</td>
                  <td className="num cell-mute">
                    {c.method === 'DECLINING'
                      ? `${c.rate}٪ سنويًا`
                      : c.method === 'STRAIGHT_LINE'
                        ? `${c.usefulLifeMonths} شهرًا`
                        : '—'}
                  </td>
                  <td className="num cell-mute" dir="ltr">
                    {byId.get(c.assetAccountId)?.code ?? '—'}
                  </td>
                  <td className="num">{c.assetCount}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-sm" onClick={() => setEditing(c)}>
                        تعديل
                      </button>
                      {c.assetCount === 0 && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => remove.mutate(c.id)}
                        >
                          حذف
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!categories.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا تصنيفات"
            text="ابدأ من التصنيفات المقترحة بنسب شائعة في مصر، أو أنشئ تصنيفك."
          />
        )}
      </div>

      <p className="tiny mute mt-3">
        {/*
          السياسة تُنسخ إلى الأصل وقت تسجيله ولا تُقرأ من التصنيف بعدها،
          فتعديلها لا يمسّ إهلاكًا رُحِّل إلى الدفتر.
        */}
        تعديل السياسة يسري على ما يُسجَّل بعده. الأصول المسجَّلة تحتفظ بسياستها وقت تسجيلها،
        فلا يتغيّر إهلاك رُحِّل إلى الدفتر.
      </p>

      {(creating || editing) && (
        <CategoryDialog
          category={editing}
          accounts={accounts.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onDone={() => {
            refresh();
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function CategoryDialog({
  category,
  accounts,
  onClose,
  onDone,
}: {
  category: Category | null;
  accounts: AccountOption[];
  onClose: () => void;
  onDone: () => void;
}) {
  const editing = Boolean(category);
  const [name, setName] = useState(category?.name ?? '');
  const [method, setMethod] = useState<DepreciationMethod>(category?.method ?? 'DECLINING');
  const [rate, setRate] = useState(String(category?.rate ?? 25));
  const [life, setLife] = useState(String(category?.usefulLifeMonths ?? 48));
  const [assetAccountId, setAsset] = useState(category?.assetAccountId ?? '');
  const [accumulatedAccountId, setAccum] = useState(category?.accumulatedAccountId ?? '');
  const [expenseAccountId, setExpense] = useState(category?.expenseAccountId ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        method,
        rate: Number(rate) || 0,
        usefulLifeMonths: Number(life) || 1,
        assetAccountId,
        accumulatedAccountId,
        expenseAccountId,
      };
      return editing
        ? api(`/assets/categories/${category!.id}`, { method: 'PATCH', body })
        : api('/assets/categories', { method: 'POST', body });
    },
    onSuccess: onDone,
  });

  const valid =
    name.trim().length >= 2 && assetAccountId && accumulatedAccountId && expenseAccountId;

  const options = (filter?: string) =>
    accounts
      .filter((a) => !filter || a.code.startsWith(filter))
      .map((a) => (
        <option key={a.id} value={a.id}>
          {a.code} — {a.name}
        </option>
      ));

  return (
    <Modal
      title={editing ? `تعديل ${category!.name}` : 'تصنيف جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {accounts.length === 0 && (
        <div className="alert alert-warning mb-4">
          دليل الحسابات غير مهيّأ. هيّئه من الدفتر العام أولًا — التصنيف بلا حسابات لا يُرحَّل
          إهلاكه.
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="cat-name">
            اسم التصنيف
          </label>
          <input
            id="cat-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="cat-method">
            طريقة الإهلاك
          </label>
          <select
            id="cat-method"
            className="select"
            value={method}
            onChange={(e) => setMethod(e.target.value as DepreciationMethod)}
          >
            {DEPRECIATION_METHODS.map((m) => (
              <option key={m} value={m}>
                {DEPRECIATION_METHOD_MAP[m].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{DEPRECIATION_METHOD_MAP[method].description}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="cat-rate">
            {method === 'DECLINING' ? 'النسبة السنوية ٪' : 'العمر الإنتاجي بالشهور'}
          </label>
          {method === 'DECLINING' ? (
            <input
              id="cat-rate"
              className="input num"
              dir="ltr"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          ) : (
            <input
              id="cat-rate"
              className="input num"
              dir="ltr"
              inputMode="numeric"
              value={life}
              onChange={(e) => setLife(e.target.value)}
              disabled={method === 'NONE'}
            />
          )}
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="cat-asset">
            حساب الأصل
          </label>
          <select
            id="cat-asset"
            className="select"
            value={assetAccountId}
            onChange={(e) => setAsset(e.target.value)}
          >
            <option value="">اختر</option>
            {options('12')}
          </select>
          <span className="tiny mute">مجموعة الأصول الثابتة — ١٢xx.</span>
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="cat-accum">
            حساب مجمع الإهلاك
          </label>
          <select
            id="cat-accum"
            className="select"
            value={accumulatedAccountId}
            onChange={(e) => setAccum(e.target.value)}
          >
            <option value="">اختر</option>
            {options('12')}
          </select>
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="cat-expense">
            حساب مصروف الإهلاك
          </label>
          <select
            id="cat-expense"
            className="select"
            value={expenseAccountId}
            onChange={(e) => setExpense(e.target.value)}
          >
            <option value="">اختر</option>
            {options('5')}
          </select>
        </div>
      </div>

      {/*
        القيد الذي سيُكتب كل شهر — عرضه هنا يجعل أثر الاختيار مفهومًا قبل
        الحفظ لا بعد أول ترحيل.
      */}
      <div className="alert alert-info mt-4">
        كل شهر يُكتب قيد: من حساب مصروف الإهلاك إلى حساب مجمع الإهلاك، بقيمة أقساط أصول هذا
        التصنيف.
      </div>
    </Modal>
  );
}
